// src/services/referralStorage.js
// Single source of truth for capturing/reading/clearing the referral code
// that a new user picks up from a referral link before signing up.

const STORAGE_KEY = 'referralCode';
const TIMESTAMP_KEY = 'referralCodeTimestamp';

// Codes are valid for 90 days after capture — matches the "Attribution
// window: 90 days from link click or signup" copy shown on the referral
// page. Long enough for slow-converting users; short enough that a stale
// tab won't carry a code forever.
const TTL_MS = 90 * 24 * 60 * 60 * 1000;

// Backend format: EB-XXXXXX (32-char alphabet, 6 chars after the prefix).
const CODE_REGEX = /^EB-[A-Z0-9]{6}$/;

const normalize = (raw) => {
    if (typeof raw !== 'string') return null;
    const upper = raw.trim().toUpperCase();
    return CODE_REGEX.test(upper) ? upper : null;
};

/**
 * Capture a referral code from the current URL's `?ref=` query param.
 * Safe to call from any top-level component's mount effect.
 */
export const captureReferralCodeFromUrl = (search = window.location.search) => {
    try {
        const params = new URLSearchParams(search);
        const code = normalize(params.get('ref'));
        if (!code) return null;
        localStorage.setItem(STORAGE_KEY, code);
        localStorage.setItem(TIMESTAMP_KEY, Date.now().toString());
        return code;
    } catch {
        return null;
    }
};

/**
 * Return the stored code, or null if absent / malformed / expired.
 * Auto-clears stale entries so callers never see expired data.
 */
export const getStoredReferralCode = () => {
    try {
        const code = normalize(localStorage.getItem(STORAGE_KEY));
        if (!code) return null;
        const ts = parseInt(localStorage.getItem(TIMESTAMP_KEY) || '0', 10);
        if (!ts || Date.now() - ts > TTL_MS) {
            clearStoredReferralCode();
            return null;
        }
        return code;
    } catch {
        return null;
    }
};

export const clearStoredReferralCode = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(TIMESTAMP_KEY);
    } catch {
        /* ignore */
    }
};
